import { format, isSameDay, startOfDay } from 'date-fns';
import { Check, FacebookIcon, InstagramIcon, Loader2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { SingleCalendar } from '@/components/ui/single-calendar';
import { cn } from '@/lib/utils';

import type { MetaAdsPage } from '@/features/integrations';

import { acceptBatchItemForm } from '../../api/accept-batch-item';

/** Labels come from the form declaration — the contract locates by these strings. */
const L = acceptBatchItemForm.labels;

function isFutureTime(day: Date, hour: number, minute: number, now: number) {
  const date = new Date(day);
  date.setHours(hour, minute, 0, 0);
  // Also exclude wall-clock times skipped by a daylight-saving transition.
  return (
    date.getHours() === hour &&
    date.getMinutes() === minute &&
    date.getTime() > now
  );
}

function PageToggle({
  page,
  checked,
  onToggle,
}: {
  page: MetaAdsPage;
  checked: boolean;
  onToggle: () => void;
}) {
  const Icon = page.platform === 'instagram' ? InstagramIcon : FacebookIcon;
  return (
    <button
      type="button"
      onClick={onToggle}
      // `aria-pressed` rather than a nested <Checkbox>: the shadcn checkbox
      // renders its own <button>, and a button inside a button is invalid DOM
      // that React warns about — the inner control also swallowed the click
      // target for anyone navigating by keyboard.
      aria-pressed={checked}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
        checked
          ? 'border-primary bg-primary/10'
          : 'border-border hover:bg-muted'
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
          checked
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-input'
        )}
      >
        {checked ? <Check className="size-3" /> : null}
      </span>
      <Icon
        className={cn(
          'size-4',
          page.platform === 'instagram' ? 'text-pink-600' : 'text-blue-600'
        )}
      />
      <span className="max-w-[140px] truncate">
        {page.pageName ?? page.platform}
      </span>
    </button>
  );
}

export interface ScheduleControlsProps {
  /**
   * Classes for the TRIGGER, so a host can size it to sit with its neighbours.
   *
   * The content panel puts it beside Reject and Save at `h-7`; left at the
   * default the three buttons were three different heights in one row.
   */
  triggerClassName?: string;
  pages: MetaAdsPage[];
  selectedPageIds: string[];
  onTogglePage: (pageId: string) => void;
  scheduleAt: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  onTimeChange: (value: string) => void;
  /** Accept the post at the chosen date and time. */
  onConfirm: () => void;
  canConfirm: boolean;
  isConfirming?: boolean;
}

/**
 * Where and when to post. Shared body for the desktop popover and the mobile
 * sheet — one implementation so the two can't drift.
 */
function ScheduleControlsBody({
  pages,
  selectedPageIds,
  onTogglePage,
  scheduleAt,
  onDateChange,
  onTimeChange,
  onConfirm,
  canConfirm,
  isConfirming,
}: ScheduleControlsProps) {
  const timeId = useId();
  const errorId = useId();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const nextMinute = new Date(Math.floor(now / 60_000) * 60_000 + 60_000);
  const selectedHour = scheduleAt?.getHours();
  const selectedMinute = scheduleAt?.getMinutes();
  const selectedTime = scheduleAt ? format(scheduleAt, 'HH:mm') : '';
  // Keep an empty or invalid edit visible without submitting the previous time.
  // A new date/time from the parent replaces the edit, without a sync effect.
  const [timeEdit, setTimeEdit] = useState<{
    scheduledAt: number | undefined;
    value: string;
  }>();
  const timeValue =
    timeEdit && timeEdit.scheduledAt === scheduleAt?.getTime()
      ? timeEdit.value
      : selectedTime;
  const isFuture = Boolean(
    scheduleAt &&
      timeValue &&
      timeValue === selectedTime &&
      scheduleAt.getTime() > now
  );

  const selectDate = (day: Date | undefined) => {
    if (!day) return onDateChange(undefined);
    const currentTime = Date.now();
    const earliest = new Date(
      Math.floor(currentTime / 60_000) * 60_000 + 60_000
    );
    // Recheck when clicked in case the calendar stayed open past midnight.
    if (startOfDay(day).getTime() < startOfDay(earliest).getTime()) return;
    const date = new Date(day);
    date.setHours(
      selectedHour ?? earliest.getHours(),
      selectedMinute ?? earliest.getMinutes(),
      0,
      0
    );
    setTimeEdit(undefined);
    // Choosing today must not initially select midnight (or an earlier time
    // carried over from another day). Start at the next available minute.
    onDateChange(date.getTime() > currentTime ? date : earliest);
  };

  const selectTime = (value: string) => {
    setTimeEdit({ scheduledAt: scheduleAt?.getTime(), value });
    const [nextHour, nextMinute] = value.split(':').map(Number);
    if (
      scheduleAt &&
      value &&
      isFutureTime(scheduleAt, nextHour, nextMinute, Date.now())
    ) {
      onTimeChange(value);
    }
  };

  return (
    <div className="space-y-4">
      <SingleCalendar
        selected={scheduleAt}
        disabled={{ before: startOfDay(nextMinute) }}
        onSelect={selectDate}
        className="w-full p-0"
      />

      <div className="space-y-1.5">
        <Label htmlFor={timeId} className="text-xs">
          Time
        </Label>
        <Input
          id={timeId}
          type="time"
          step={60}
          value={timeValue}
          min={
            scheduleAt && isSameDay(scheduleAt, nextMinute)
              ? format(nextMinute, 'HH:mm')
              : undefined
          }
          disabled={!scheduleAt}
          aria-invalid={Boolean(scheduleAt && !isFuture)}
          aria-describedby={scheduleAt && !isFuture ? errorId : undefined}
          onChange={(event) => selectTime(event.target.value)}
          className="tabular-nums"
        />
        {scheduleAt && !isFuture && (
          <p id={errorId} role="alert" className="text-xs text-destructive">
            {timeValue
              ? 'Choose a date and time in the future.'
              : 'Choose a time.'}
          </p>
        )}
        {!scheduleAt && (
          <span className="text-xs text-muted-foreground">
            Pick a date to set the time.
          </span>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">{L.targetPageIds}</Label>
        {pages.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No connected pages. Connect a Facebook or Instagram page to schedule
            this post.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {pages.map((page) => (
              <PageToggle
                key={page.id}
                page={page}
                checked={selectedPageIds.includes(page.id)}
                onToggle={() => onTogglePage(page.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* The commit lives WITH the fields it commits. A Schedule button outside
          the popover would let someone pick a date, dismiss, and hit it without
          ever seeing what they'd chosen. */}
      <Button
        type="button"
        onClick={() => {
          const currentTime = Date.now();
          setNow(currentTime);
          if (
            !scheduleAt ||
            !timeValue ||
            timeValue !== selectedTime ||
            scheduleAt.getTime() <= currentTime
          )
            return;
          onConfirm();
        }}
        disabled={!canConfirm || !isFuture || isConfirming}
        className="w-full gap-2"
      >
        {isConfirming ? <Loader2 className="size-4 animate-spin" /> : null}
        Schedule
      </Button>
    </div>
  );
}

/**
 * A popover on desktop, a sheet on mobile.
 *
 * Date, time and the page picker need real room, and a dialog inside a dialog
 * can never give it to them — which is the whole reason the review surface is a
 * route rather than a modal.
 */
export function ScheduleControls(
  props: ScheduleControlsProps & { isMobile: boolean }
) {
  const { isMobile, triggerClassName, ...body } = props;

  // The trigger always reads "Schedule". It used to swap to the chosen
  // date/time once one was picked, which made the primary action's label change
  // under the user mid-task and left the pair reading "Save / Thu 30 Jul,
  // 19:15" — two buttons that no longer look like the same kind of choice. The
  // chosen time is shown inside the popover, where it is being edited.
  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button type="button" className={cn('w-full', triggerClassName)}>
            Schedule
          </Button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          className="max-h-[90dvh] overflow-y-auto overscroll-contain p-4"
        >
          <SheetHeader className="px-0">
            <SheetTitle>Schedule this post</SheetTitle>
            <SheetDescription>
              Choose when and where to publish.
            </SheetDescription>
          </SheetHeader>
          <ScheduleControlsBody {...body} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" className={triggerClassName}>
          Schedule
        </Button>
      </PopoverTrigger>
      {/* Opens UPWARDS: the trigger sits in the footer at the bottom of the
          viewport, so a downward popover would open off-screen. */}
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        collisionPadding={16}
        className="max-h-[var(--radix-popover-content-available-height)] w-80 max-w-[calc(100vw-2rem)] overflow-y-auto overscroll-contain"
      >
        <ScheduleControlsBody {...body} />
      </PopoverContent>
    </Popover>
  );
}
